<script setup lang="tsx">
  import CustomDropdown from '~/components/UI/Forms/CustomDropdown.vue';
  import CustomInput from '~/components/UI/Forms/CustomInput.vue';
  import RoleBasedButton from '~/components/UI/Others/RoleBasedButton.vue';
  import { get, post } from '~/services/donationScreen';
  import type {
    GetResponse,
    ResponseStatus,
    OrgBankDrawer,
    PostResponse,
    OrgBankLedgerOption,
    OrgBankDropdownData
  } from '~/types';
  import EditPasswordModal from '../../Modals/EditPasswordModal.vue';
  import AuditLogs from '../RoutedDrawerComponents/AuditLogs.vue';

  const props = defineProps<{
    onClose: () => void;
    identifier: string;
  }>();

  const route = useRoute();

  const action = computed(() => route.query.action as string);

  // Organization Bank ledgers are always the fixed "Expense Ledger" type, so it is never shown/selected in the form
  const FIXED_LEDGER_TYPE = 'cash-bank';

  const formRef = ref();
  const formState = reactive({
    alias: '',
    accountNo: '',
    accountHolderName: '',
    bankName: '',
    ifscCode: '',
    bookOfAccounts: '',
    ledgerName: '',
    auditLogData: [] as any[]
  });
  const status = ref<boolean>(false);
  const isModalVisible = ref<boolean>(false);
  const isModalVisibleForDisable = ref<boolean>(false);
  const editStatus = ref<ResponseStatus>('idle');

  const skipLedgerNameReset = ref(false);
  const ledgerList = ref<OrgBankLedgerOption[]>([]);

  // Backend already restricts this list to active, fixed-type ledgers - only the Book of Accounts match is applied client-side
  const ledgerNameOptions = computed(() =>
    ledgerList.value
      .filter(ledger => ledger.is_fcra === formState.bookOfAccounts && !ledger.is_disabled)
      .map(ledger => ({ title: ledger.ledger_name, value: ledger.ledger_name }))
  );

  const fetchLedgerList = async () => {
    try {
      const response = await get<GetResponse<OrgBankDropdownData>>('v1/get_dropdown_data_org_bank');
      ledgerList.value = response?.data?.data?.ledgers_list || [];
    } catch (error) {
      console.error('Error fetching ledger list:', error);
    }
  };

  const fetchData = async () => {
    try {
      const response = await get<GetResponse<OrgBankDrawer>>(`v1/get_org_bank/${props.identifier}`);
      status.value = response?.data?.data?.selected_org_bank?.is_disabled;
      if (response.data) {
        ledgerList.value = response?.data?.data?.ledgers_list || [];
        skipLedgerNameReset.value = true;
        Object.assign(formState, {
          alias: response?.data?.data?.selected_org_bank?.alias || '',
          accountNo: response?.data?.data?.selected_org_bank?.account_no || '',
          accountHolderName: response?.data?.data?.selected_org_bank?.account_holder_name || '',
          bankName: response?.data?.data?.selected_org_bank?.bank_name || '',
          ifscCode: response?.data?.data?.selected_org_bank?.ifsc_code || '',
          bookOfAccounts: response?.data?.data?.selected_org_bank?.book_of_accounts || '',
          ledgerName: response?.data?.data?.selected_org_bank?.ledger_name || '',
          auditLogData: response?.data?.data?.audit_log_data || []
        });
        await nextTick();
        skipLedgerNameReset.value = false;
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  watch(
    () => formState.bookOfAccounts,
    () => {
      if (skipLedgerNameReset.value) return;
      formState.ledgerName = '';
    }
  );

  const accountNumberPattern = /^(?:\d{9,18}|[A-Za-z0-9]{9,30})$/;
  const ifscPattern = /^[A-Z]{4}0[A-Z0-9]{6}$/;

  const rules = {
    alias: [{ required: true, message: 'Reference Name (Alias) is required', trigger: 'submit' }],
    accountNo: [
      { required: true, message: 'Account No is required', trigger: 'submit' },
      {
        validator: (_: any, value: string) => {
          if (!value) return Promise.resolve();
          if (!accountNumberPattern.test(value))
            return Promise.reject(
              'Invalid account number. Use 9–18 digits or 9–30 alphanumeric characters (letters and digits only, no spaces or special characters).'
            );
          return Promise.resolve();
        },
        trigger: 'submit'
      }
    ],
    accountHolderName: [{ required: true, message: 'Account Holder Name is required', trigger: 'submit' }],
    bankName: [{ required: true, message: 'Bank Name is required', trigger: 'submit' }],
    ifscCode: [
      { required: true, message: 'IFSC Code is required', trigger: 'submit' },
      {
        validator: (_: any, value: string) => {
          if (!value) return Promise.resolve();
          if (!ifscPattern.test(value)) return Promise.reject('Please enter a valid IFSC code (e.g., ABCD0123456)');
          return Promise.resolve();
        },
        trigger: 'submit'
      }
    ],
    bookOfAccounts: [{ required: true, message: 'Book of Accounts is required', trigger: 'submit' }]
  };

  const updateStateField = (value: string, stateKey: keyof typeof formState) => {
    (formState as any)[stateKey] = value;
  };

  const submitHandler = async (modalData: { password: string }) => {
    try {
      editStatus.value = 'pending';
      const response = await post<PostResponse<OrgBankDrawer>>(`v1/org_bank/${props.identifier}`, {
        data: {
          alias: formState.alias,
          account_no: formState.accountNo,
          account_holder_name: formState.accountHolderName,
          bank_name: formState.bankName,
          ifsc_code: formState.ifscCode,
          book_of_accounts: formState.bookOfAccounts,
          ledger_type: FIXED_LEDGER_TYPE,
          ledger_name: formState.ledgerName,
          password: modalData.password
        }
      });

      // AxiosError returned by handleError on 4xx — .response.data has the backend payload
      if ((response as any)?.response?.data) {
        editStatus.value = 'error';
        isModalVisible.value = false;
        const httpStatus = (response as any)?.response?.status;
        if (httpStatus === 401) return openFailedCustomNotification('Wrong Password!');
        return openFailedCustomNotification(extractErrorMessage(response, 'Failed to Update Organization Bank'));
      }

      if (response?.data?.status === 'success') {
        openSuccessfulCustomNotification('Organization Bank Updated Successfully');
        isModalVisible.value = false;
        props.onClose();
      } else {
        editStatus.value = 'error';
        isModalVisible.value = false;
        openFailedCustomNotification(response?.data?.message || 'Failed to Update Organization Bank');
      }
    } catch (error: any) {
      editStatus.value = 'error';
      openFailedCustomNotification(extractErrorMessage(error, 'Failed to Update Organization Bank'));
    }
  };

  const extractErrorMessage = (response: any, fallback: string): string => {
    // post() returns AxiosError on 4xx (handleError returns the error object itself)
    const axiosErrMsg = response?.response?.data?.message;
    if (axiosErrMsg) return axiosErrMsg;
    // Normal success/error response from backend
    const dataMsg = response?.data?.message;
    if (dataMsg) return dataMsg;
    return fallback;
  };

  const handleCreate = async () => {
    editStatus.value = 'pending';
    try {
      const response = await post<PostResponse<OrgBankDrawer>>('v1/add_new_org_bank', {
        data: {
          alias: formState.alias,
          account_no: formState.accountNo,
          account_holder_name: formState.accountHolderName,
          bank_name: formState.bankName,
          ifsc_code: formState.ifscCode,
          book_of_accounts: formState.bookOfAccounts,
          ledger_type: FIXED_LEDGER_TYPE,
          ledger_name: formState.ledgerName
        }
      });

      // If the service returned an AxiosError (4xx/5xx), .response.data holds the backend payload
      if ((response as any)?.response?.data) {
        editStatus.value = 'error';
        return openFailedCustomNotification(extractErrorMessage(response, 'Failed to Create Organization Bank'));
      }

      if (response?.data?.status === 'success') {
        openSuccessfulCustomNotification('Organization Bank Created Successfully');
        props.onClose();
        editStatus.value = 'success';
      } else {
        editStatus.value = 'error';
        openFailedCustomNotification(response?.data?.message || 'Failed to Create Organization Bank');
      }
    } catch (error: any) {
      editStatus.value = 'error';
      openFailedCustomNotification(extractErrorMessage(error, 'Failed to Create Organization Bank'));
    }
  };

  const handleDisableOrgBank = async (modalData: { password: string }) => {
    editStatus.value = 'pending';
    try {
      const response = await post<any>(`/v1/org_bank_disabled/${props.identifier}`, {
        data: {
          is_disabled: true,
          password: modalData.password
        }
      });

      if (response.status === 401) {
        editStatus.value = 'error';
        return openFailedCustomNotification('Wrong Password!');
      }

      if (response.data.status === 'success') {
        openSuccessfulCustomNotification('Organization Bank Inactive Successfully');
        isModalVisibleForDisable.value = false;
        props.onClose();
        editStatus.value = 'success';
      } else {
        editStatus.value = 'error';
        openFailedCustomNotification(response.data.message || 'Failed to set Organization Bank Inactive');
      }
    } catch (error) {
      console.error(error);
      openFailedCustomNotification('Failed to set Organization Bank Inactive');
      editStatus.value = 'error';
    }
  };

  const handleActiveOrgBank = async () => {
    editStatus.value = 'pending';
    try {
      const response = await post<any>(`/v1/org_bank_enabled/${props.identifier}`, {
        data: { is_disabled: false }
      });

      if (response.status === 401) {
        editStatus.value = 'error';
        return openFailedCustomNotification('Wrong Password!');
      }

      if (response.data.status === 'success') {
        openSuccessfulCustomNotification('Organization Bank Activated Successfully');
        isModalVisibleForDisable.value = false;
        props.onClose();
        editStatus.value = 'success';
      } else {
        editStatus.value = 'error';
        openFailedCustomNotification(response.data.message || 'Failed to Activate Organization Bank');
      }
    } catch (error) {
      console.error(error);
      openFailedCustomNotification('Failed to Activate Organization Bank');
      editStatus.value = 'error';
    }
  };

  const handleSave = async () => {
    try {
      await formRef.value.validate();
      if (action.value === 'edit') return openModal();
      await handleCreate();
    } catch (error) {
      console.error(error);
    }
  };

  const openModal = async () => {
    try {
      const isFormValid = await formRef.value.validate();
      if (!isFormValid) throw new Error('Form validation failed!');
      isModalVisible.value = true;
    } catch (error) {
      console.error(error);
    }
  };

  const handleModalCancel = () => {
    isModalVisible.value = false;
  };
  const handleModalDisableCancel = () => {
    isModalVisibleForDisable.value = false;
  };

  onMounted(() => {
    if (props.identifier) {
      fetchData();
    } else {
      fetchLedgerList();
    }
  });

  // RoutedDrawerController keeps this same component instance mounted when the user
  // switches from one bank row to another (only the `identifier` query param changes,
  // `drawerFrom` stays 'orgBanks') — without this, the drawer kept showing the
  // previously opened bank's data (including its Audit Log) instead of refetching.
  watch(
    () => props.identifier,
    (newIdentifier, oldIdentifier) => {
      if (newIdentifier === oldIdentifier) return;
      if (newIdentifier) {
        fetchData();
      } else {
        Object.assign(formState, {
          alias: '',
          accountNo: '',
          accountHolderName: '',
          bankName: '',
          ifscCode: '',
          bookOfAccounts: '',
          ledgerName: '',
          auditLogData: []
        });
        status.value = false;
        fetchLedgerList();
      }
    }
  );
</script>

<template>
  <a-drawer
    :open="route.query.drawerFrom === 'orgBanks'"
    :title="
      action === 'edit'
        ? 'Update Organization Bank'
        : action === 'add' || action === 'duplicate'
          ? 'Add New Organization Bank'
          : 'Organization Bank'
    "
    width="750"
    class="monetary-drawer"
    :body-style="{ padding: '0px' }"
    :header-style="{ padding: '12px', borderBottom: '1px solid #E4E4E7' }"
    @close="props.onClose"
  >
    <template #extra>
      <div class="flex gap-3">
        <RoleBasedButton
          v-if="!props.identifier || action"
          component-name="addActionButton"
          active-class="py-2 px-3 bg-blue-600 hover:bg-blue-700 text-white rounded sm-medium duration-150 border border-blue-600"
          disabled-class="py-2 px-3 rounded sm-medium duration-150 !bg-gray-200 !shadow-[0px_1px_3px_0px_#0000001A,0px_1px_2px_0px_#0000000F] border-none"
          :loading="editStatus === 'pending'"
          @click="handleSave"
        >
          {{ action === 'edit' ? 'Update Organization Bank' : 'Create Organization Bank' }}
        </RoleBasedButton>
        <RoleBasedButton
          v-if="action === 'edit' && !status"
          component-name="addActionButton"
          active-class="py-2 px-3 bg-red-600 hover:bg-red-700 text-white rounded sm-medium duration-150 border border-red-600 hover:!text-white hover:!border-red-700"
          disabled-class="py-2 px-3 rounded sm-medium duration-150 !bg-gray-200 !shadow-[0px_1px_3px_0px_#0000001A,0px_1px_2px_0px_#0000000F] border-none"
          :loading="editStatus === 'pending'"
          @click="isModalVisibleForDisable = true"
        >
          Set Inactive Organization Bank
        </RoleBasedButton>
        <RoleBasedButton
          v-if="action === 'edit' && status"
          component-name="addActionButton"
          active-class="py-2 px-3 bg-green-600 hover:bg-green-700 text-white rounded sm-medium duration-150 border border-green-600 hover:!text-white hover:!border-green-700"
          disabled-class="py-2 px-3 rounded sm-medium duration-150 !bg-gray-200 !shadow-[0px_1px_3px_0px_#0000001A,0px_1px_2px_0px_#0000000F] border-none"
          :loading="editStatus === 'pending'"
          @click="handleActiveOrgBank"
        >
          Set Active Organization Bank
        </RoleBasedButton>
      </div>
    </template>

    <a-form
      ref="formRef"
      class="flex flex-col gap-4 p-3 setup-drawer-form"
      :model="formState"
      :rules="rules"
      :colon="false"
      :label-col="{ style: { width: '250px', textAlign: 'left' } }"
      hide-required-mark="true"
    >
      <CustomInput
        label-text="Reference Name (Alias)"
        name="alias"
        :value="formState.alias"
        state-key="alias"
        :is-compulsory="true"
        :is-disabled="!action"
        @update:value="updateStateField"
      />
      <CustomInput
        label-text="Account No"
        name="accountNo"
        :value="formState.accountNo"
        state-key="accountNo"
        :is-compulsory="true"
        :is-disabled="!action"
        @update:value="updateStateField"
      />
      <CustomInput
        label-text="Account Holder Name"
        name="accountHolderName"
        :value="formState.accountHolderName"
        state-key="accountHolderName"
        :is-compulsory="true"
        :is-disabled="!action"
        @update:value="updateStateField"
      />
      <CustomInput
        label-text="Bank Name"
        name="bankName"
        :value="formState.bankName"
        state-key="bankName"
        :is-compulsory="true"
        :is-disabled="!action"
        @update:value="updateStateField"
      />
      <CustomInput
        label-text="IFSC Code"
        name="ifscCode"
        :value="formState.ifscCode"
        state-key="ifscCode"
        :is-compulsory="true"
        :is-disabled="!action"
        @update:value="updateStateField"
      />
      <CustomDropdown
        :options="[
          { title: 'FCRA Books', value: 'FCRA Books' },
          { title: 'Non-FCRA Books', value: 'Non-FCRA Books' }
        ]"
        label-text="Book of Accounts"
        :label-width="'250px'"
        name="bookOfAccounts"
        :value="formState.bookOfAccounts"
        state-key="bookOfAccounts"
        :rule="rules.bookOfAccounts"
        :is-compulsory="true"
        :disabled="!action"
        @update:value="updateStateField"
      />
      <CustomDropdown
        :options="ledgerNameOptions"
        label-text="Ledger Name"
        :label-width="'250px'"
        name="ledgerName"
        :value="formState.ledgerName"
        state-key="ledgerName"
        :disabled="!action || !formState.bookOfAccounts"
        @update:value="updateStateField"
      />
    </a-form>

    <AuditLogs
      v-if="action !== 'add' && action !== 'duplicate'"
      :items="formState.auditLogData"
      title="Organization Bank"
      class="mx-3"
    />

    <EditPasswordModal
      :visible="isModalVisible"
      :loading="editStatus === 'pending'"
      modal-title="Update Organization Bank"
      @update:cancel="handleModalCancel"
      @update:update="submitHandler"
    />
    <EditPasswordModal
      :visible="isModalVisibleForDisable"
      :loading="editStatus === 'pending'"
      modal-title="Inactive Organization Bank"
      :warning-message="'Are you sure you want to set this Organization Bank Inactive?'"
      @update:cancel="handleModalDisableCancel"
      @update:update="handleDisableOrgBank"
    />
  </a-drawer>
</template>

<style>
  @import '~/assets/css/donationDrawer.css';

  .setup-drawer-form .ant-form-item {
    margin-bottom: 0px;
  }
</style>
